<script lang="ts">
	import {
		filterScenesInitial,
		generateThermostatIcon,
		sortBy,
		sortListByPossition,
	} from 'src/components/Lists/list-helpers';
	import SelectList from 'src/components/SelectList/SelectList.svelte';
	import { format } from 'svelte-i18n';
	import { scenes } from 'src/stores/lists/scenes';
	import { user_data, user_settings } from 'src/stores/account/user';
	import { devices } from 'src/stores/lists/devices';
	import { groups } from 'src/stores/lists/groups';
	import { rooms } from 'src/stores/lists/rooms';
	import { alarms, temp_schemes } from 'src/stores/lists/scenes-derived';
	import { createEventDispatcher } from 'svelte';
	import WeatherLocationModal from 'src/components/Weather/WeatherLocationModal.svelte';
	import { Alert } from '@Shelly/shelly-design-system';

	export let edit;
	export let selectedType;
	export let selectItems;
	export let allItemsRefs;
	export let filterDevicesByRoomID;
	export let widgetsCounter;

	let showModal = false;

	const dispatch = createEventDispatcher();

	$: list = getListByType(selectedType.id, selectItems) || [];

	const getListByType = (type, items) => {
		const isPremium = $user_data.premium || $user_settings.been_premium;
		const maxCardsAllowed = isPremium === 1 ? 80 : 40;
		const disableCard = widgetsCounter >= maxCardsAllowed && !$edit;

		const isCardChecked = (id) => items.some((obj) => obj.id === id);

		if (type === 'rooms') {
			return sortListByPossition(Object.values($rooms)).map((room) => {
				return {
					...room,
					disabled: !isCardChecked(room.id) && disableCard,
				};
			});
		} else if (type === 'groups') {
			return sortListByPossition(Object.values($groups)).map((group) => {
				return {
					...group,
					disabled: !isCardChecked(group.id) && disableCard,
				};
			});
		} else if (type === 'scenes') {
			return sortScenesListByPossition(
				Object.values($scenes)
					.filter(filterScenesInitial)
					.map((scene) => {
						return {
							...scene,
							name: scene._meta.name,
							image: scene._meta.image,
							backgroundColor: scene._meta.backgroundColor,
							id: scene._id,
							disabled: !isCardChecked(scene.id) && disableCard,
						};
					}),
			);
		} else if (type === 'devices') {
			return roomsFiltered.map((room) => {
				return {
					...room,
					cards: room.cards.map((card) => {
						return {
							...card,
							disabled: !isCardChecked(card.id) && disableCard,
						};
					}),
				};
			});
		} else if (type === 'alarms') {
			return sortScenesListByPossition(
				Object.values($alarms).map((alarm) => {
					return {
						...alarm,
						name: alarm._meta.name,
						image: alarm._meta.image,
						backgroundColor: alarm._meta.backgroundColor,
						id: alarm._id,
						disabled: !isCardChecked(alarm.id) && disableCard,
					};
				}),
			);
		} else if (type === 'temp_schemes') {
			return sortScenesListByPossition(
				Object.values($temp_schemes).map((thermostat) => {
					return {
						...thermostat,
						name: thermostat._meta.name,
						icon: generateThermostatIcon(thermostat),
						id: thermostat._id,
						disabled: !isCardChecked(thermostat.id) && disableCard,
					};
				}),
			);
		}
	};

	$: roomsFiltered = sortListByPossition(filterDevicesByRoomID(Object.values($devices)));

	$: title = $edit
		? $format('select_items_by_type', {
				values: {
					item: $format(selectedType.i18n_sub_name),
				},
			})
		: $format('label_selected', {
				default: 'Selected:',
			});

	$: titleHighlight = $edit ? '' : selectItems.length;

	const sortScenesListByPossition = (list: any) => {
		return list.sort((x: any, y: any) => {
			return sortBy(x._meta, y._meta, 'position');
		});
	};

	const handleSelectItem = (device: object = {}, id: number, type: string) => {
		dispatch('selectedItem', { device, id, type });
	};
</script>

{#if showModal}
	<WeatherLocationModal bind:showModal />
{/if}

{#if selectedType.id === 'weather'}
	<Alert
		type="info"
		heading={$format('alert_heading_dashboard_wizard_weather_info', {
			default: 'Do you want to change your weather info?',
		})}
		cta={{
			title: `${$format('alert_cta_dashboard_wizard_weather_info_link', {
				default: 'Go to weather info',
			})}`,
		}}
		on:cta_click={() => {
			showModal = true;
		}}
	/>
{:else}
	<SelectList
		{title}
		{titleHighlight}
		{list}
		variant={$edit ? 'radio' : 'checkbox'}
		selectedCardsId={selectItems}
		bind:allCardsRefs={allItemsRefs}
		on:click={(e) => handleSelectItem(e.detail, e.detail.id, selectedType.id)}
	/>
{/if}
